import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Search,
  Filter,
  RefreshCw,
  X,
  CheckCircle2,
  Clock3,
  AlertCircle,
  Wallet,
  Receipt,
  Edit3,
  RotateCcw,
  IndianRupee,
  ChevronDown,
  ChevronRight,
  Calendar,
  Undo2,
  FileText,
  Loader2,
} from 'lucide-react';

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatINR = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

export default function ReferralsListPage() {
  const { user, isAdmin, canUpdatePayments, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: { count: 0, amount: 0 }, paid: { count: 0, amount: 0 }, total: { count: 0, amount: 0 } });
  const [doctors, setDoctors] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    q: '',
    date_from: '',
    date_to: '',
    referral_doctor_id: '',
    marketing_executive_id: '',
    consultant_id: '',
    village: '',
    payment_status: '',
    voucher_number: '',
  });

  const [selectedRef, setSelectedRef] = useState(null); // referral list item
  const [detailPayment, setDetailPayment] = useState(null); // { referral, payment }

  useEffect(() => {
    // Load reference dropdowns once
    const load = async () => {
      try {
        const [d, e, c] = await Promise.all([
          axios.get(`${API_BASE}/referral-doctors?status_filter=active&limit=2000`, { headers: getAuthHeader() }),
          axios.get(`${API_BASE}/marketing-executives?status_filter=active&limit=2000`, { headers: getAuthHeader() }),
          axios.get(`${API_BASE}/inhouse-consultants?status_filter=active&limit=2000`, { headers: getAuthHeader() }),
        ]);
        setDoctors((d.data || []).map((r) => ({ id: r.id, name: r.name, subtitle: r.village })));
        setExecutives((e.data || []).map((r) => ({ id: r.id, name: r.name, subtitle: r.village_area })));
        setConsultants((c.data || []).map((r) => ({ id: r.id, name: r.name, subtitle: r.specialty })));
      } catch (_err) {}
    };
    load();
    // eslint-disable-next-line
  }, []);

  const canSeeSummary = user?.role === 'admin' || user?.role === 'accountant';
  const canSeeTotals = !['reception', 'lab_staff', 'pharmacist'].includes(user?.role);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v.toString().trim()) params[k] = v; });
      const listRes = await axios.get(`${API_BASE}/referrals`, { headers: getAuthHeader(), params });
      setRows(listRes.data || []);
      if (canSeeSummary) {
        try {
          const summaryRes = await axios.get(`${API_BASE}/reports/payment-summary`, {
            headers: getAuthHeader(),
            params: {
              date_from: params.date_from || undefined,
              date_to: params.date_to || undefined,
              referral_doctor_id: params.referral_doctor_id || undefined,
              marketing_executive_id: params.marketing_executive_id || undefined,
            },
          });
          setSummary(summaryRes.data);
        } catch (_) {
          // silently ignore — role may not have access
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load referrals.');
    } finally {
      setLoading(false);
    }
  }, [filters, getAuthHeader, canSeeSummary]);

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const resetFilters = () => {
    setFilters({
      q: '', date_from: '', date_to: '',
      referral_doctor_id: '', marketing_executive_id: '', consultant_id: '',
      village: '', payment_status: '', voucher_number: '',
    });
  };

  const applyDatePreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const toISO = (dt) => dt.toISOString().slice(0, 10);
    let from, to;
    if (preset === 'today') { from = to = toISO(new Date(y, m, d)); }
    else if (preset === 'yesterday') { const y1 = new Date(y, m, d - 1); from = to = toISO(y1); }
    else if (preset === 'this_week') {
      const dayOfWeek = now.getDay() || 7;
      from = toISO(new Date(y, m, d - dayOfWeek + 1));
      to = todayISO();
    } else if (preset === 'this_month') { from = toISO(new Date(y, m, 1)); to = todayISO(); }
    else if (preset === 'prev_month') { from = toISO(new Date(y, m - 1, 1)); to = toISO(new Date(y, m, 0)); }
    else { from = to = ''; }
    update({ date_from: from, date_to: to });
  };

  const openPayment = async (row) => {
    setSelectedRef(row);
    try {
      const [refRes, payRes] = await Promise.all([
        axios.get(`${API_BASE}/referrals/${row.id}`, { headers: getAuthHeader() }),
        axios.get(`${API_BASE}/referrals/${row.id}/payment`, { headers: getAuthHeader() }),
      ]);
      setDetailPayment({ referral: refRes.data, payment: payRes.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load referral details.');
    }
  };

  const closeDetail = () => {
    setSelectedRef(null);
    setDetailPayment(null);
  };

  const afterPaymentUpdate = (message) => {
    setSuccess(message);
    fetchList();
    // Refresh the detail modal
    if (selectedRef) openPayment(selectedRef);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <AppHeader />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
            <Wallet className="w-6 h-6" />
          </div>
          <div>
            <h1 data-testid="referrals-list-title" className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {canSeeTotals ? 'Referrals & Payments' : 'Referrals'}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {canSeeTotals
                ? 'Filter, search, and update payment status across every referral in the system.'
                : 'Filter and search referrals you have access to.'}
            </p>
          </div>
        </div>

        {/* Summary cards — Admin + Accountant only */}
        {canSeeSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryCard
            title="Total Referrals"
            count={summary.total.count}
            amount={summary.total.amount}
            testId="summary-total"
            colorClass="from-slate-900 to-slate-950 border-slate-800 text-slate-100"
            icon={<Receipt className="w-4 h-4" />}
          />
          <SummaryCard
            title="Pending Payments"
            count={summary.pending.count}
            amount={summary.pending.amount}
            testId="summary-pending"
            onClick={() => update({ payment_status: filters.payment_status === 'pending' ? '' : 'pending' })}
            active={filters.payment_status === 'pending'}
            colorClass="from-amber-950/60 to-slate-900 border-amber-800/60 text-amber-100"
            icon={<Clock3 className="w-4 h-4" />}
          />
          <SummaryCard
            title="Cleared Payments"
            count={summary.paid.count}
            amount={summary.paid.amount}
            testId="summary-paid"
            onClick={() => update({ payment_status: filters.payment_status === 'paid' ? '' : 'paid' })}
            active={filters.payment_status === 'paid'}
            colorClass="from-emerald-950/60 to-slate-900 border-emerald-800/60 text-emerald-100"
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
        </div>
        )}
        {/* Alerts */}
        {success && (
          <div data-testid="referrals-success-alert" className="p-3 bg-emerald-950/60 border border-emerald-800/80 rounded-xl text-emerald-200 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto text-emerald-400/70 hover:text-emerald-200"><X className="w-4 h-4" /></button>
          </div>
        )}
        {error && (
          <div data-testid="referrals-error-alert" className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-400/70 hover:text-red-200"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'this_week', label: 'This Week' },
              { key: 'this_month', label: 'This Month' },
              { key: 'prev_month', label: 'Previous Month' },
              { key: '', label: 'All Time' },
            ].map((p) => (
              <button
                key={p.key || 'all'}
                type="button"
                onClick={() => applyDatePreset(p.key)}
                data-testid={`date-preset-${p.key || 'all'}`}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 hover:border-teal-700 text-slate-300 hover:text-white transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="filter-search"
                type="text"
                placeholder="Search by patient name, OP number, or referral ID (QR-…)"
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
                className="w-full pl-10 pr-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
            <input
              data-testid="filter-date-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => update({ date_from: e.target.value })}
              className="px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
            <input
              data-testid="filter-date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => update({ date_to: e.target.value })}
              className="px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchableSelect testId="filter-doctor" options={doctors} value={filters.referral_doctor_id} onChange={(id) => update({ referral_doctor_id: id })} placeholder="All Referral Doctors" />
            <SearchableSelect testId="filter-executive" options={executives} value={filters.marketing_executive_id} onChange={(id) => update({ marketing_executive_id: id })} placeholder="All Marketing Executives" />
            <SearchableSelect testId="filter-consultant" options={consultants} value={filters.consultant_id} onChange={(id) => update({ consultant_id: id })} placeholder="All Consultants" />
            <input
              data-testid="filter-village"
              type="text"
              placeholder="Village / area"
              value={filters.village}
              onChange={(e) => update({ village: e.target.value })}
              className="px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {canSeeTotals && (
              <select
                data-testid="filter-payment-status"
                value={filters.payment_status}
                onChange={(e) => update({ payment_status: e.target.value })}
                className="px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-slate-200 focus:outline-none focus:border-teal-500"
              >
                <option value="">All Payment Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            )}
            {canSeeTotals && (
              <input
                data-testid="filter-voucher"
                type="text"
                placeholder="Voucher number"
                value={filters.voucher_number}
                onChange={(e) => update({ voucher_number: e.target.value })}
                className="px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            )}
            <button
              onClick={resetFilters}
              data-testid="reset-filters-btn"
              className="px-3 py-2.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white rounded-xl text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
            </button>
            <button
              onClick={fetchList}
              data-testid="refresh-list-btn"
              className="px-3 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 rounded-xl text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="referrals-table">
              <thead>
                <tr className="bg-slate-950/80 border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-400">
                  <th className="py-3 px-4 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Referral ID</th>
                  <th className="py-3 px-4 font-semibold">Patient</th>
                  <th className="py-3 px-4 font-semibold">Referral Doctor</th>
                  <th className="py-3 px-4 font-semibold">Executive</th>
                  {canSeeTotals && <th className="py-3 px-4 font-semibold text-right">Amount</th>}
                  {canSeeTotals && <th className="py-3 px-4 font-semibold">Status</th>}
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-sm">
                {rows.length === 0 ? (
                  <tr><td colSpan={canSeeTotals ? 8 : 6} className="py-10 text-center text-slate-500">{loading ? 'Loading…' : 'No referrals match the current filters.'}</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} data-testid={`referral-row-${r.id}`} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-3 px-4 text-slate-300 text-xs">{r.referral_date || formatDate(r.created_at)}</td>
                      <td className="py-3 px-4 font-mono text-teal-300 text-xs">{r.referral_id}</td>
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{r.patient_name}</div>
                        <div className="text-[11px] text-slate-500">OP {r.op_number}{r.village ? ` · ${r.village}` : ''}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-300 text-xs">{r.referral_doctor_name || '—'}</td>
                      <td className="py-3 px-4 text-slate-300 text-xs">{r.marketing_executive_name || '—'}</td>
                      {canSeeTotals && (
                        <td className="py-3 px-4 text-right text-white font-semibold whitespace-nowrap">
                          <span className="inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3 opacity-60" />{formatINR(r.final_referral_amount)}</span>
                        </td>
                      )}
                      {canSeeTotals && (
                        <td className="py-3 px-4">
                          <StatusPill status={r.payment_status} />
                        </td>
                      )}
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openPayment(r)}
                          data-testid={`open-payment-btn-${r.id}`}
                          className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 rounded-lg transition-colors inline-flex items-center gap-1.5"
                        >
                          Open <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <div className="px-4 py-2 text-[11px] text-slate-500 flex items-center gap-2 border-t border-slate-800/60">
              <Filter className="w-3 h-3" />
              <span data-testid="referrals-count">Showing {rows.length} referral{rows.length === 1 ? '' : 's'}</span>
            </div>
          )}
        </div>
      </main>

      {selectedRef && detailPayment && (
        <ReferralDetailModal
          referral={detailPayment.referral}
          payment={detailPayment.payment}
          canUpdatePayments={canUpdatePayments}
          canSeeTotals={canSeeTotals}
          isAdmin={isAdmin}
          onClose={closeDetail}
          onNavigateEdit={() => navigate(`/staff/referrals/${detailPayment.referral.id}/edit`)}
          onUpdated={afterPaymentUpdate}
          onError={(msg) => setError(msg)}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
}

function SummaryCard({ title, count, amount, testId, colorClass = '', icon, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      className={`text-left bg-gradient-to-br ${colorClass} border rounded-xl p-4 transition-all ${
        onClick ? 'hover:scale-[1.01] cursor-pointer' : 'cursor-default'
      } ${active ? 'ring-2 ring-teal-400/60' : ''}`}
    >
      <div className="flex items-center gap-2 text-xs opacity-80 uppercase tracking-widest mb-1">
        {icon}<span>{title}</span>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div data-testid={`${testId}-count`} className="text-2xl font-bold tabular-nums">{count}</div>
          <div className="text-[11px] opacity-70">referral{count === 1 ? '' : 's'}</div>
        </div>
        <div className="text-right">
          <div data-testid={`${testId}-amount`} className="text-lg font-semibold tabular-nums flex items-center justify-end gap-0.5">
            <IndianRupee className="w-3.5 h-3.5 opacity-70" />{formatINR(amount)}
          </div>
        </div>
      </div>
    </button>
  );
}

function StatusPill({ status }) {
  const isPaid = status === 'paid';
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full font-semibold ${
        isPaid ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80' : 'bg-amber-950/80 text-amber-300 border border-amber-800/80'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isPaid ? 'bg-emerald-400' : 'bg-amber-400'}`} />
      {isPaid ? 'Paid' : 'Pending'}
    </span>
  );
}

function ReferralDetailModal({ referral, payment, canUpdatePayments, canSeeTotals, isAdmin, onClose, onNavigateEdit, onUpdated, onError, getAuthHeader }) {
  const { user } = useAuth();
  const canEditReferral = ['admin', 'lab_staff', 'pharmacist', 'reception'].includes(user?.role);
  const editLabel = user?.role === 'lab_staff' ? 'Update Lab Section' : user?.role === 'pharmacist' ? 'Update Pharmacy Section' : user?.role === 'reception' ? 'Update Patient / Scan / IP' : 'Edit Referral';
  const initialTab = canSeeTotals ? 'summary' : 'summary';
  const [tab, setTab] = useState(initialTab); // summary | payment | history
  const [saving, setSaving] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [payDate, setPayDate] = useState(todayISO());
  const [voucher, setVoucher] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [note, setNote] = useState('');
  const [reverseNote, setReverseNote] = useState('');
  const [modalError, setModalError] = useState('');

  const isPaid = payment.payment_status === 'paid';

  const handleMarkPaid = async () => {
    setModalError('');
    if (!payDate) { setModalError('Payment date is required.'); return; }
    if (!voucher.trim()) { setModalError('Voucher number is required.'); return; }
    if (!receivedBy.trim()) { setModalError('Referral Payment Received By is required.'); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/referrals/${referral.id}/payment`,
        {
          payment_status: 'paid',
          payment_date: payDate,
          voucher_number: voucher.trim(),
          referral_payment_received_by: receivedBy.trim(),
          note: note.trim() || undefined,
        },
        { headers: getAuthHeader() }
      );
      onUpdated(`Referral ${referral.referral_id} marked as Paid.`);
      setNote(''); setVoucher(''); setReceivedBy(''); setPayDate(todayISO());
    } catch (err) {
      setModalError(err.response?.data?.detail || 'Failed to update payment.');
    } finally {
      setSaving(false);
    }
  };

  const handleReverse = async () => {
    if (!window.confirm('Reverse this payment back to Pending? An entry will be added to the audit trail.')) return;
    setReversing(true);
    setModalError('');
    try {
      await axios.post(
        `${API_BASE}/referrals/${referral.id}/payment/reverse`,
        { note: reverseNote.trim() || undefined },
        { headers: getAuthHeader() }
      );
      onUpdated(`Payment for ${referral.referral_id} reversed to Pending.`);
      setReverseNote('');
    } catch (err) {
      setModalError(err.response?.data?.detail || 'Failed to reverse payment.');
    } finally {
      setReversing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="referral-detail-modal" className="bg-slate-900 border border-slate-800 w-full max-w-3xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-5 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-teal-400">Referral</div>
            <div className="font-mono text-2xl font-bold text-white">{referral.referral_id}</div>
            <div className="text-xs text-slate-400 mt-1">{referral.patient_name} · OP {referral.op_number}{referral.village ? ` · ${referral.village}` : ''}</div>
          </div>
          <div className="flex items-center gap-2">
            {canSeeTotals && <StatusPill status={payment.payment_status} />}
            <button onClick={onClose} data-testid="close-detail-btn" className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="border-b border-slate-800 flex gap-1 px-4">
          {[
            { id: 'summary', label: 'Summary' },
            canSeeTotals && { id: 'payment', label: 'Payment' },
            canSeeTotals && { id: 'history', label: `Audit Trail (${payment.history?.length || 0})` },
          ].filter(Boolean).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`detail-tab-${t.id}`}
              className={`text-xs px-3 py-2.5 border-b-2 transition-colors ${
                tab === t.id ? 'border-teal-400 text-teal-300' : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {modalError && (
            <div className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{modalError}
            </div>
          )}

          {tab === 'summary' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <KV label="Referral Doctor" value={referral.referral_doctor_name || '—'} />
                <KV label="Marketing Executive" value={referral.marketing_executive_name || '—'} />
                <KV label="Consultant" value={referral.consultant_name || '—'} />
              </div>
              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4 space-y-2 text-sm">
                <SectionLine label="Scan" total={referral.scan_total_amount} ref_={referral.scan_referral_amount} note="manual" showDerived={canSeeTotals} />
                <SectionLine label="Lab" total={referral.lab_total_amount} kit={referral.lab_kit_amount} net={referral.lab_net_amount} pct={referral.lab_percentage} ref_={referral.lab_referral_amount} showDerived={canSeeTotals} />
                <SectionLine label="Pharmacy" total={referral.pharmacy_total_amount} kit={referral.pharmacy_kit_amount} net={referral.pharmacy_net_amount} pct={referral.pharmacy_percentage} ref_={referral.pharmacy_referral_amount} showDerived={canSeeTotals} />
                <SectionLine label="IP" total={referral.ip_amount} kit={referral.ip_deductibles} kitLabel="deduct." net={referral.ip_net_amount} pct={referral.ip_percentage} ref_={referral.ip_referral_amount} showDerived={canSeeTotals} />
              </div>
              {canSeeTotals && (
                <div className="bg-teal-950/50 border border-teal-800/60 rounded-xl p-4 flex items-center justify-between">
                  <div className="text-xs text-teal-200 uppercase tracking-widest">Final Referral Amount</div>
                  <div className="text-2xl font-bold text-white flex items-center gap-1"><IndianRupee className="w-5 h-5 text-teal-300" />{formatINR(referral.final_referral_amount)}</div>
                </div>
              )}
              <div className="text-[11px] text-slate-500">
                Created by <span className="text-slate-300">{referral.created_by}</span> on {formatDateTime(referral.created_at)}
                {referral.updated_at !== referral.created_at && (
                  <> · Updated by <span className="text-slate-300">{referral.updated_by}</span> on {formatDateTime(referral.updated_at)}</>
                )}
              </div>
              {canEditReferral && (
                <div className="pt-2">
                  <button onClick={onNavigateEdit} data-testid="modal-edit-referral-btn" className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 rounded-lg inline-flex items-center gap-1.5">
                    <Edit3 className="w-3.5 h-3.5" /> {editLabel}
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === 'payment' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <KV label="Patient Name" value={referral.patient_name || '—'} />
                <KV label="Referral ID" value={referral.referral_id || '—'} />
                <KV label="Referral Doctor" value={referral.referral_doctor_name || '—'} />
                <KV label="Total Referral Amount" value={`₹${formatINR(referral.final_referral_amount)}`} />
                <KV label="Current Status" value={payment.payment_status === 'paid' ? 'Paid' : 'Pending'} />
                <KV label="Payment Date" value={payment.payment_date ? formatDate(payment.payment_date) : '—'} />
                <KV label="Voucher Number" value={payment.voucher_number || '—'} />
                <KV label="Referral Payment Received By" value={payment.referral_payment_received_by || '—'} />
                <KV label="Processed By / Accountant" value={payment.payment_received_by_name || '—'} />
                <KV label="Last Updated" value={payment.updated_at ? formatDateTime(payment.updated_at) : '—'} />
              </div>

              {!isPaid && canUpdatePayments && (
                <div className="border-t border-slate-800 pt-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2"><Receipt className="w-4 h-4 text-teal-400" /> Record Payment</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Payment Date *</label>
                      <input data-testid="payment-date-input" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Voucher Number *</label>
                      <input data-testid="voucher-input" type="text" value={voucher} onChange={(e) => setVoucher(e.target.value)} placeholder="e.g. V-2026-042" className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Referral Payment Received By *</label>
                    <input
                      data-testid="referral-payment-received-by-input"
                      type="text"
                      value={receivedBy}
                      onChange={(e) => setReceivedBy(e.target.value)}
                      placeholder="e.g. Ramesh Kumar"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                      Enter the actual person who received the referral payment (not you).
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Note (optional)</label>
                    <input data-testid="payment-note-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Cash / cheque / mode of payment…" className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Processed By / Accountant: <span className="text-slate-300">You — {user?.name} (@{user?.username})</span> — captured automatically from your session.
                  </div>
                  <button
                    onClick={handleMarkPaid}
                    disabled={saving}
                    data-testid="mark-paid-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 text-sm font-semibold rounded-xl shadow-md shadow-teal-500/20 transition-all"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {saving ? 'Saving…' : 'Mark as Paid'}
                  </button>
                </div>
              )}

              {!isPaid && !canUpdatePayments && (
                <div className="text-xs text-slate-400 border-t border-slate-800 pt-3">
                  Only Accountants and Admins can record payments. Ask them to update this referral.
                </div>
              )}

              {isPaid && isAdmin && (
                <div className="border-t border-slate-800 pt-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2"><Undo2 className="w-4 h-4 text-rose-400" /> Reverse Payment</h4>
                  <p className="text-xs text-slate-400">Move this payment back to Pending. The full audit history is preserved and a "reversed" entry is added.</p>
                  <input data-testid="reverse-note-input" type="text" value={reverseNote} onChange={(e) => setReverseNote(e.target.value)} placeholder="Reason for reversal…" className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500" />
                  <button
                    onClick={handleReverse}
                    disabled={reversing}
                    data-testid="reverse-payment-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-rose-950/60 hover:bg-rose-900/60 disabled:opacity-50 text-rose-200 border border-rose-800/80 text-sm font-semibold rounded-xl transition-all"
                  >
                    {reversing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                    {reversing ? 'Reversing…' : 'Reverse to Pending'}
                  </button>
                </div>
              )}

              {isPaid && !isAdmin && (
                <div className="text-xs text-slate-400 border-t border-slate-800 pt-3">
                  This payment is Paid. Only an Admin can reverse a paid payment.
                </div>
              )}
            </div>
          )}

          {tab === 'history' && (
            <div className="space-y-3">
              {(payment.history || []).length === 0 ? (
                <div className="text-xs text-slate-500">No history entries yet.</div>
              ) : (
                [...payment.history].reverse().map((h, i) => (
                  <div key={i} data-testid={`history-entry-${i}`} className="p-3 bg-slate-950/60 border border-slate-800 rounded-xl">
                    <div className="flex items-center justify-between text-xs">
                      <span className={`inline-flex items-center gap-1.5 font-semibold ${
                        h.action === 'marked_paid' ? 'text-emerald-300' : h.action === 'reversed' ? 'text-rose-300' : 'text-slate-300'
                      }`}>
                        {h.action === 'marked_paid' && <CheckCircle2 className="w-3.5 h-3.5" />}
                        {h.action === 'reversed' && <Undo2 className="w-3.5 h-3.5" />}
                        {h.action === 'created' && <FileText className="w-3.5 h-3.5" />}
                        {h.action === 'marked_paid' ? 'Marked as Paid' : h.action === 'reversed' ? 'Payment Reversed' : 'Referral Created'}
                      </span>
                      <span className="text-slate-500">{formatDateTime(h.at)}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      by <span className="text-slate-200">{h.by_name || h.by_username}</span>
                      {h.voucher_number && <> · Voucher <span className="font-mono text-teal-300">{h.voucher_number}</span></>}
                      {h.payment_date && <> · Payment date {formatDate(h.payment_date)}</>}
                      {h.referral_payment_received_by && <> · Received by <span className="text-slate-200">{h.referral_payment_received_by}</span></>}
                    </div>
                    {h.note && <div className="text-[11px] text-slate-500 mt-1 italic">"{h.note}"</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm text-white font-medium truncate mt-0.5">{value}</div>
    </div>
  );
}

function SectionLine({ label, total, kit, net, pct, ref_, kitLabel = 'kit', note, showDerived = true }) {
  const hasData = total || kit || ref_;
  if (!hasData) {
    return (
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span>{label}</span><span>—</span>
      </div>
    );
  }
  const rawSummary = `total ₹${formatINR(total || 0)}${kit ? `, ${kitLabel} ₹${formatINR(kit)}` : ''}`;
  const derivedSummary = `${net ? `, net ₹${formatINR(net)}` : ''}${pct ? `, ${pct}%` : ''}`;
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="text-slate-300">
        <span className="font-medium">{label}</span>
        <span className="text-slate-500 ml-2">
          {note ? note : `${rawSummary}${showDerived ? derivedSummary : ''}`}
        </span>
      </div>
      {showDerived && <div className="text-teal-300 font-semibold whitespace-nowrap">₹{formatINR(ref_ || 0)}</div>}
    </div>
  );
}
